"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { MEGA_MENUS, activeMenuKey, type MegaMenu } from "@/design-data/nav-menus";
import { Z_HEADER } from "@/components/layout/zIndex";
import { HelplineCompactButton } from "./HelplineButton";
import { HomeBadge } from "./HomeBadge";
import { MobileNav } from "./MobileNav";
import { useIsInternationalVisitor } from "@/greenpak/use-visitor-country";
import { useCartCount } from "@/greenpak/use-cart-count";
import { ThemeToggle } from "./ThemeToggle";
import { onHomeClick } from "./home-click";
import { SearchSuggestInput } from "@/components/search/SearchSuggestInput";

const MOBILE_NAV_ID = "site-mobile-nav";
/** Matches the min-[1180px] classes below — the desktop/mobile switch. */
const DESKTOP_QUERY = "(min-width: 1180px)";
/** Grace period before a mouse-opened dropdown closes, so crossing a pixel
 *  gap or overshooting the panel edge does not snap it shut. */
const HOVER_CLOSE_MS = 150;

function pathOnly(href: string): string {
  return href.split(/[?#]/)[0] || "/";
}

/**
 * Whether the header logo is also a link home. Off since the Home badge
 * joined the menu (owner, 1 Oct 2026: "if required will enable it at that
 * time") — set to true to make the logo a home link again.
 */
const LOGO_LINKS_HOME = false;

function BrandMark({ onNavigate }: { onNavigate?: () => void }) {
  const className = "flex flex-col items-center gap-0.5 sm:gap-1 no-underline text-[var(--chrome-fg)] shrink-0";
  const content = (
    <>
      <span className="sr-only">
        Green Tourism Pakistan
        <span className="inline-flex h-[0.82em] w-[0.82em] mx-[0.05em] translate-y-[0.04em] items-center justify-center">
          <Image src="/images/globe.png" alt="o" width={56} height={56} className="h-full w-full object-contain" />
        </span>
      </span>
      <Image
        src="/images/greenpak-flower.png"
        alt=""
        width={72}
        height={72}
        aria-hidden
        priority
        className="block h-9 w-9 sm:h-12 sm:w-12 shrink-0 object-contain"
      />
      <span className="flex flex-col items-center text-center text-[0.72rem] sm:text-[0.92rem] font-bold leading-[1.12] tracking-wide whitespace-nowrap text-[var(--chrome-fg)]">
        <span>Green Tourism</span>
        <span>Pakistan</span>
      </span>
      <span className="text-[8px] sm:text-[9px] font-semibold uppercase tracking-[0.2em] text-[var(--cta)]">Beta</span>
    </>
  );
  if (!LOGO_LINKS_HOME) return <div className={className}>{content}</div>;
  return (
    <Link
      href="/"
      onClick={() => {
        // Clicking the logo should always feel like "go home" — Next's Link
        // doesn't reset scroll when the target is already the current route
        // (or is cached), so without this a tap while scrolled deep into a
        // long page (e.g. the map section) looks like nothing happened.
        window.scrollTo({ top: 0, behavior: "instant" });
        onNavigate?.();
      }}
      className={className}
    >
      {content}
    </Link>
  );
}

/**
 * One desktop top-level item. The label is always an ordinary link to the
 * section page; items with a dropdown get a separate chevron button that
 * opens it, so touch and keyboard users can reach both. Mouse hover opens it
 * too, but only for real mice — a touch tap also fires emulated hover events,
 * which would open the menu and then let the same tap's click toggle it shut.
 */
function DesktopNavItem({
  menu,
  pathname,
  isActive,
  isOpen,
  alignRight,
  scrolled,
  onOpen,
  onToggle,
  onClose,
  onHoverOpen,
  onHoverClose,
}: {
  menu: MegaMenu;
  pathname: string;
  isActive: boolean;
  isOpen: boolean;
  alignRight: boolean;
  scrolled: boolean;
  onOpen: () => void;
  onToggle: () => void;
  onClose: () => void;
  onHoverOpen: () => void;
  onHoverClose: () => void;
}) {
  const links = menu.columns.flatMap((c) => c.links);
  const hasMenu = links.length > 0;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLUListElement>(null);
  const panelId = `nav-panel-${menu.key}`;
  const highlighted = isActive || isOpen;

  function focusLink(index: number) {
    const items = panelRef.current?.querySelectorAll<HTMLAnchorElement>("a");
    if (!items?.length) return;
    items[(index + items.length) % items.length]?.focus();
  }

  function onButtonKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      onOpen();
      // The panel is in the DOM already (just `hidden`); wait one frame for
      // it to become visible before moving focus into it.
      requestAnimationFrame(() => focusLink(0));
    }
  }

  function onPanelKeyDown(e: KeyboardEvent<HTMLUListElement>) {
    const items = Array.from(panelRef.current?.querySelectorAll<HTMLAnchorElement>("a") ?? []);
    const index = items.indexOf(document.activeElement as HTMLAnchorElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusLink(index + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusLink(index - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusLink(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusLink(items.length - 1);
    } else if (e.key === "Escape") {
      onClose();
      buttonRef.current?.focus();
    }
  }

  const mouseOnly = (fn: () => void) => (e: PointerEvent) => {
    if (e.pointerType === "mouse" && hasMenu) fn();
  };

  return (
    <li
      className="relative flex items-center"
      onPointerEnter={mouseOnly(onHoverOpen)}
      onPointerLeave={mouseOnly(onHoverClose)}
      onBlur={(e) => {
        // Tabbing past the last link (or anywhere outside the item) closes it.
        if (isOpen && !e.currentTarget.contains(e.relatedTarget as Node | null)) onClose();
      }}
    >
      <Link
        href={menu.href}
        onClick={(e) => onHomeClick(e, pathname, menu.href)}
        aria-current={pathname === menu.href ? "page" : undefined}
        aria-label={menu.key === "home" ? menu.label : undefined}
        title={menu.key === "home" ? menu.label : undefined}
        className={`group inline-block whitespace-nowrap border-b-2 text-sm font-semibold tracking-[0.16em] uppercase no-underline transition-all duration-200 hover:text-[var(--cta)] ${
          menu.key === "home" ? (scrolled ? "py-[6px]" : "py-[18px]") : scrolled ? "py-[14px]" : "py-[26px]"
        }`}
        style={{
          color: highlighted ? "var(--chrome-fg)" : "var(--chrome-muted)",
          borderBottomColor: isActive ? "var(--cta)" : "transparent",
        }}
      >
        {menu.key === "home" ? <HomeBadge /> : menu.label}
      </Link>

      {hasMenu ? (
        <>
          <button
            ref={buttonRef}
            type="button"
            aria-expanded={isOpen}
            aria-controls={panelId}
            aria-label={`${menu.label} pages`}
            onClick={onToggle}
            onKeyDown={onButtonKeyDown}
            className="ml-0.5 inline-flex h-8 w-6 cursor-pointer items-center justify-center rounded bg-transparent transition-colors duration-150 hover:text-[var(--cta)]"
            style={{ color: highlighted ? "var(--chrome-fg)" : "var(--chrome-muted)" }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              className={`transition-transform duration-200 motion-reduce:transition-none ${isOpen ? "rotate-180" : ""}`}
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>

          <ul
            ref={panelRef}
            id={panelId}
            hidden={!isOpen}
            onKeyDown={onPanelKeyDown}
            className={`pbd-menu-in absolute top-full m-0 min-w-[240px] list-none rounded-b-lg border border-t-0 border-[var(--nav-menu-border)] bg-[var(--nav-menu-bg)] p-2 shadow-[0_18px_40px_rgba(0,0,0,0.28)] ${
              alignRight ? "right-0" : "-left-4"
            }`}
          >
            {links.map((link) => {
              const current = pathname === pathOnly(link.href);
              return (
                <li key={`${link.href}::${link.label}`}>
                  <Link
                    href={link.href}
                    onClick={onClose}
                    aria-current={current ? "page" : undefined}
                    className={`block whitespace-nowrap rounded px-3 py-2.5 text-sm no-underline transition-colors duration-150 hover:bg-[color-mix(in_srgb,var(--nav-menu-fg)_7%,transparent)] hover:text-[var(--nav-menu-fg)] focus-visible:bg-[color-mix(in_srgb,var(--nav-menu-fg)_7%,transparent)] ${
                      current ? "text-[var(--cta)]" : "text-[var(--nav-menu-muted)]"
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </li>
  );
}

export function SiteNav({ menus = MEGA_MENUS }: { menus?: MegaMenu[] }) {
  // The helpline number and the trip badge are both decided in the browser, so
  // the page around them can be cached (see app/(site)/layout.tsx).
  const liveIsInternational = useIsInternationalVisitor();
  const cartCount = useCartCount();
  const router = useRouter();
  const pathname = usePathname() ?? "/";
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // How the open dropdown was opened. A mouse reaches the chevron by hovering
  // over the item, which already opened it — so a click on a hover-opened menu
  // pins it open instead of toggling it shut, and a pinned menu ignores
  // mouse-leave (it closes on outside click, Escape, or the chevron again).
  const openedBy = useRef<"hover" | "click">("click");

  const activeKey = activeMenuKey(menus, pathname);

  function clearHoverTimer() {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  }

  // Condense the header once the page scrolls, the weather bar + 110px nav
  // claims a lot of viewport on laptops otherwise.
  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Any navigation closes whatever is open. Adjusting state during render
  // (rather than in an effect) avoids a frame where the old menu is still
  // showing over the new page.
  const [lastPathname, setLastPathname] = useState(pathname);
  if (pathname !== lastPathname) {
    setLastPathname(pathname);
    setOpenMenu(null);
    setMobileOpen(false);
  }

  // Escape closes the open dropdown / mobile menu (keyboard accessibility).
  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        setOpenMenu(null);
        setMobileOpen(false);
        setSearchOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // A click or tap anywhere outside the desktop nav closes its dropdown.
  useEffect(() => {
    if (!openMenu) return;
    function onPointerDown(e: globalThis.PointerEvent) {
      if (!navRef.current?.contains(e.target as Node)) setOpenMenu(null);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [openMenu]);

  // Widening past the breakpoint with the mobile menu open would leave the
  // page scroll-locked behind a menu that is no longer shown.
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    function onChange() {
      if (mq.matches) setMobileOpen(false);
      else setOpenMenu(null);
    }
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => clearHoverTimer, []);

  return (
    <header
      className={`sticky top-0 ${Z_HEADER} bg-[var(--chrome-bg)] backdrop-blur-[18px] border-b border-[var(--chrome-border)] text-[var(--chrome-fg)]`}
    >
      <div
        // Tighter padding/gap on phones: at 320px the brand + two helpline
        // pills + menu button only fit with 16px sides and 12px gaps.
        className={`flex items-center justify-between gap-3 sm:gap-5 max-w-[1400px] mx-auto px-4 sm:px-6 transition-[min-height] duration-200 ${
          scrolled ? "min-h-[64px] sm:min-h-[72px]" : "min-h-[82px] sm:min-h-[110px]"
        }`}
      >
        <BrandMark onNavigate={() => setMobileOpen(false)} />

        <nav ref={navRef} aria-label="Main" className="hidden min-[1180px]:flex self-stretch">
          <ul className="m-0 flex list-none items-stretch gap-5 p-0 xl:gap-8">
            {menus.map((menu, index) => (
              <DesktopNavItem
                key={menu.key}
                menu={menu}
                pathname={pathname}
                isActive={activeKey === menu.key}
                isOpen={openMenu === menu.key}
                alignRight={index === menus.length - 1}
                scrolled={scrolled}
                onOpen={() => {
                  clearHoverTimer();
                  openedBy.current = "click";
                  setOpenMenu(menu.key);
                }}
                onToggle={() => {
                  clearHoverTimer();
                  if (openMenu === menu.key && openedBy.current === "click") {
                    setOpenMenu(null);
                  } else {
                    openedBy.current = "click";
                    setOpenMenu(menu.key);
                  }
                }}
                onClose={() => {
                  clearHoverTimer();
                  setOpenMenu((k) => (k === menu.key ? null : k));
                }}
                onHoverOpen={() => {
                  clearHoverTimer();
                  if (openMenu === menu.key) return;
                  openedBy.current = "hover";
                  setOpenMenu(menu.key);
                }}
                onHoverClose={() => {
                  clearHoverTimer();
                  if (openedBy.current !== "hover") return;
                  hoverTimer.current = setTimeout(
                    () => setOpenMenu((k) => (k === menu.key ? null : k)),
                    HOVER_CLOSE_MS,
                  );
                }}
              />
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-1.5 xl:gap-2.5 shrink-0">
          <div
            className={`hidden min-[1180px]:flex items-center overflow-hidden rounded border border-[var(--chrome-border)] transition-[width] duration-200 ease-out ${
              searchOpen ? "w-[230px] bg-[var(--chrome-surface)]" : "w-10 bg-transparent"
            }`}
          >
            <button
              type="button"
              aria-label="Search"
              onClick={() => {
                setSearchOpen(true);
                searchInputRef.current?.focus();
              }}
              tabIndex={searchOpen ? -1 : 0}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center text-[var(--chrome-muted)] cursor-pointer transition-colors duration-150 hover:text-[var(--chrome-fg)]"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.5" />
                <path d="m16 16 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <SearchSuggestInput
              ref={searchInputRef}
              type="search"
              aria-label="Search the site"
              placeholder="Search…"
              value={searchQuery}
              onValueChange={setSearchQuery}
              onPicked={() => {
                setSearchQuery("");
                setSearchOpen(false);
                searchInputRef.current?.blur();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const q = searchQuery.trim();
                  if (q) {
                    router.push(`/search?q=${encodeURIComponent(q)}`);
                  }
                  setSearchOpen(false);
                }
                if (e.key === "Escape") {
                  e.currentTarget.blur();
                }
              }}
              onBlur={() => setSearchOpen(false)}
              tabIndex={searchOpen ? 0 : -1}
              className="h-10 w-full min-w-0 bg-transparent pr-3 text-[13px] text-[var(--chrome-fg)] outline-none"
            />
          </div>

          {/* Below 640px the helpline pills take this slot, so the theme
              toggle moves into the mobile menu (MobileNav) instead. */}
          <span className="hidden min-[1180px]:inline-flex">
            <ThemeToggle />
          </span>

          <span className="hidden min-[1180px]:inline-flex">
            <HelplineCompactButton isInternational={liveIsInternational} />
          </span>

          <Link
            href="/account"
            aria-label="Account"
            className="hidden min-[1180px]:inline-flex w-10 h-10 items-center justify-center border border-[var(--chrome-border)] rounded text-[var(--chrome-muted)] no-underline transition-colors duration-150 hover:border-[color-mix(in_srgb,var(--chrome-fg)_28%,transparent)] hover:text-[var(--chrome-fg)]"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </Link>

          <Link
            href="/plan"
            className="hidden min-[1180px]:inline-flex items-center justify-center whitespace-nowrap min-h-10 px-[18px] rounded bg-[var(--cta)] text-[#2b2b2b] text-[13px] font-bold no-underline shadow-[0_12px_30px_color-mix(in_srgb,var(--cta)_20%,transparent)] transition-all duration-150 hover:bg-[var(--cta-bright)] hover:-translate-y-px"
          >
            Plan Your Trip
          </Link>

          {/* Phone/tablet search: a real link to /search rather than a panel,
              so it works before hydration and stays crawlable. The menu
              carries a full field for typing a query. */}
          <Link
            href="/search"
            aria-label="Search"
            className="min-[1180px]:hidden inline-flex h-10 w-10 items-center justify-center rounded border border-[var(--chrome-border)] text-[var(--chrome-muted)] no-underline"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.6" />
              <path d="m16 16 5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </Link>

          <button
            ref={mobileToggleRef}
            type="button"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls={MOBILE_NAV_ID}
            onClick={() => setMobileOpen((v) => !v)}
            // relative + after: grows the tappable area to ~48px (mobile tap-target
            // minimum) via an invisible layer, without enlarging the visible 40px button.
            className="min-[1180px]:hidden relative inline-flex w-10 h-10 items-center justify-center border border-[var(--chrome-border)] rounded bg-transparent text-[var(--chrome-fg)] cursor-pointer after:absolute after:-inset-1 after:content-['']"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden>
              {mobileOpen ? (
                <path d="m6 6 12 12M18 6 6 18" />
              ) : (
                <path d="M4 7h16M4 12h16M4 17h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Rendered inside the sticky header (not after it) so it opens right
          where the visitor already is on screen — a plain sibling block
          here would insert at the true top of the page's flow and be
          invisible without scrolling all the way up first. */}
      {mobileOpen ? (
        <MobileNav
          id={MOBILE_NAV_ID}
          menus={menus}
          activeKey={activeKey}
          pathname={pathname}
          cartCount={cartCount}
          isInternational={liveIsInternational}
          onClose={() => setMobileOpen(false)}
          returnFocusRef={mobileToggleRef}
        />
      ) : null}
    </header>
  );
}
